import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, API_URL, ApiError } from '@/lib/api';

/**
 * Settings, imports, reports and the audit log.
 *
 * The three imports and the report downloads do NOT go through `api.get`, because
 * both are file transfers rather than JSON: one sends multipart up, the other streams
 * CSV down with a Content-Disposition the browser has to see.
 */

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export interface Settings {
  codePrefix: string;
  /** The prefix an employee-owned asset gets instead, so a personal laptop reads as
   *  EMP-1007 rather than AST-1007. Each series is counted separately. */
  employeeCodePrefix: string;
  codeDigits: number;
  nextSequence: number;
  /** The employee-owned series' own counter. */
  employeeNextSequence: number;
  organizationName: string;
  /** What the next asset registered would be called. Shown so the effect of a prefix
   *  change is visible before it is saved. */
  nextCodePreview: string;
  /** The next employee-owned code, from that series' own counter - registering a
   *  company asset leaves this one where it is. */
  nextEmployeeCodePreview: string;
}

export const settingsKey = ['settings'] as const;

export function useSettings() {
  return useQuery({ queryKey: settingsKey, queryFn: () => api.get<Settings>('/settings') });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Omit<Settings, 'nextCodePreview' | 'nextEmployeeCodePreview'>>) =>
      api.patch<Settings>('/settings', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settingsKey }),
  });
}

// ---------------------------------------------------------------------------
// Bulk import
// ---------------------------------------------------------------------------

export type ImportKind = 'employees' | 'assets' | 'assignments';

export interface ImportIssue {
  /** The line number as Excel numbers it, header included. */
  line: number;
  message: string;
}

export interface ImportReport {
  imported: number;
  issues: ImportIssue[];
  clean: boolean;
}

export function useImport(kind: ImportKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<ImportReport> => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<ImportReport>(`/imports/${kind}`, form);
    },
    // Everything, because an assignment import moves assets AND people, and an asset
    // import may have created categories.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

/**
 * Templates are fetched rather than linked.
 *
 * A plain `<a href>` to the API would send no session cookie on some browsers' first
 * navigation and would show a JSON 401 in a new tab instead of downloading anything.
 * Fetching with credentials and saving the blob keeps the failure inside the page,
 * where it can be reported properly.
 */
export async function downloadTemplate(kind: ImportKind): Promise<void> {
  await downloadFile(`/imports/templates/${kind}`, `${kind}-import-template.csv`);
}

export async function downloadReport(name: string): Promise<void> {
  await downloadFile(`/reports/${name}`, `${name}.csv`);
}

async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (!response.ok) {
    throw new ApiError(response.status, 'That download failed. Try again.');
  }

  // The server names the file (it stamps the date into it); this is only the fallback.
  const disposition = response.headers.get('content-disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disposition)?.[1];

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = named ?? fallbackName;
  link.click();
  // Revoked on the next tick, not inline: Firefox and Safari read the blob
  // asynchronously after click(), and tearing the URL down synchronously cancels the
  // download. Chrome happens to tolerate it, which is how this survives testing.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const REPORTS = [
  {
    name: 'asset-register',
    title: 'Asset register',
    blurb:
      'Every asset including the archive, with purchase price, vendor and invoice number. The sheet finance reconciles against.',
  },
  {
    name: 'assigned-assets',
    title: 'Who has what',
    blurb:
      'Every asset currently issued, the person holding it, and whether they have acknowledged receiving it.',
  },
  {
    name: 'employee-register',
    title: 'Employees',
    blurb: 'The directory, with how many assets each person is holding.',
  },
  {
    name: 'repair-history',
    title: 'Repairs',
    blurb: 'Every repair with vendor, cost and parts replaced.',
  },
  {
    name: 'warranty-expiry',
    title: 'Warranty expiry',
    blurb:
      'Assets whose warranty has run out or soon will, soonest first. Assets with no warranty recorded are left out.',
  },
  {
    name: 'stock-take',
    title: 'Stock take',
    blurb:
      'The walking list: everything in service, where it should be, and how long since anyone confirmed it was there. Never-checked assets come first.',
  },
  {
    name: 'exit-clearance',
    title: 'Exit clearance',
    blurb:
      'Everyone who has left, how their clearance ended, and anything they are still holding.',
  },
] as const;

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export interface AuditEntry {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  summary: string;
  actorName: string | null;
  createdAt: string;
}

export function useAuditLog(filters: { entity?: string; action?: string } = {}) {
  const params = new URLSearchParams();
  if (filters.entity) params.set('entity', filters.entity);
  if (filters.action) params.set('action', filters.action);
  const query = params.toString();

  return useQuery({
    queryKey: ['audit', filters] as const,
    queryFn: () =>
      api.get<{ entries: AuditEntry[]; nextCursor: string | null }>(
        `/audit${query ? `?${query}` : ''}`,
      ),
  });
}
